import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, SITE_URL } from "../lib/api";
import { SEO, breadcrumbJsonLd } from "../components/SEO";
import { Layout, Breadcrumbs } from "../components/Layout";
import { PartCodeDecoder } from "../components/PartCodeDecoder";
import { useQuoteCart } from "../context/QuoteCartContext";
import { useCatalogLocale } from "../lib/localizeCatalog";
import { categoryDrawing, drawingSrc, hideDetailsSection } from "../lib/categoryDrawings";
import { lp } from "../lib/urls";

const toImperial = (v) => {
  if (!v) return v;
  return String(v)
    .replace(
      /(\d+(?:[.,]\d+)?)\s*mm\b/gi,
      (_, n) => `${(parseFloat(n.replace(",", ".")) / 25.4).toFixed(2)} in`,
    )
    .replace(
      /(\d+(?:[.,]\d+)?)\s*cm\b/gi,
      (_, n) => `${(parseFloat(n.replace(",", ".")) / 2.54).toFixed(2)} in`,
    )
    .replace(
      /(\d+(?:[.,]\d+)?)\s*kg\b/gi,
      (_, n) => `${(parseFloat(n.replace(",", ".")) * 2.20462).toFixed(2)} lb`,
    );
};

const NON_DIM_LABEL =
  /angle|number|class|type|layout|shape|style|thread pitch|nut pitch|street/i;
const NON_DIM_CODE =
  /^(type|tipo|m\.p\.type|mud holes|street holes|pattern|holes numbers|strength class|guiding type|hardened head|extremity type)$/i;

const specToImperial = (s) => {
  const val = String(s.value || "");
  if (/[º°]/.test(s.code || "") || /[º°]/.test(val)) return val;
  if (
    NON_DIM_LABEL.test(s.label || "") ||
    NON_DIM_CODE.test((s.code || "").trim())
  )
    return val;
  const m = val.trim().match(/^([A-Za-z]*)(\d+(?:[.,]\d+)?)([A-Za-z/]*)$/);
  if (m && !/^m\d/i.test(val.trim()))
    return `${m[1]}${(parseFloat(m[2].replace(",", ".")) / 25.4).toFixed(2)}${m[3]} in`;
  return toImperial(val);
};

const specToMetric = (s) => {
  const val = String(s.value || "");
  if (/[º°]/.test(s.code || "") || /[º°]/.test(val)) return val;
  if (
    NON_DIM_LABEL.test(s.label || "") ||
    NON_DIM_CODE.test((s.code || "").trim())
  )
    return val;
  if (/^\d+(?:[.,]\d+)?$/.test(val.trim())) return `${val.trim()} mm`;
  return val;
};

export default function PartDetail() {
  const { partSlug } = useParams();
  const { t } = useTranslation();
  const loc = useCatalogLocale();
  const quoteCart = useQuoteCart();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [units, setUnits] = useState("metric");
  const [drawingOpen, setDrawingOpen] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    api
      .get(`/parts/${partSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [partSlug]);

  if (notFound)
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-bold text-3xl">
            {t("cat.notFound")}
          </h1>
          <Link to={lp("/brands")} className="underline mt-4 inline-block">
            {t("cat.back")}
          </Link>
        </div>
      </Layout>
    );

  if (!data)
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">
          {t("cat.loading")}
        </div>
      </Layout>
    );

  const { part, related } = data;
  const variants = data.variants || [];
  const hideDrawing = !part.drawing_url && (part.specs?.length || 0) <= 2;
  const typeSingular = part.machinery_type.endsWith("s")
    ? part.machinery_type.slice(0, -1)
    : part.machinery_type;
  const enFullName = `${part.name} for ${part.brand_name} ${part.model_name} ${typeSingular}`;
  const fullName = t("part2.fullName", {
    part: loc.partName(part.name),
    brand: part.brand_name,
    model: part.model_name,
    type: loc.machType(part.machinery_type),
  });
  const modelPath = `/brand/${part.brand_slug}/${part.model_slug}`;
  const sizeM = part.type_key === "rubber-track" && /^VRT(.+)$/i.exec(part.part_code || "");
  const seoTitle = sizeM ? `${fullName} ${sizeM[1].toLowerCase()}` : fullName;
  const fileUrl = (u) => (u && u.startsWith("/") ? `${SITE_URL}${u}` : u);

  const crumbs = [
    { name: t("qa2.home"), path: "/" },
    { name: t("cat.brands"), path: "/brands" },
    { name: part.brand_name, path: `/brand/${part.brand_slug}` },
    { name: part.model_name, path: modelPath },
    { name: loc.partName(part.name) },
  ];

  const schemaDrawing = drawingSrc(part.drawing_url) || categoryDrawing(part.machinery_type, part.type_key);
  const jsonLd = [
    breadcrumbJsonLd(
      crumbs
        .slice(0, -1)
        .concat([{ name: part.name, path: `/part/${part.slug}` }])
        .map((c) => ({ name: c.name, path: c.path || `/part/${part.slug}` })),
    ),
  ];

  return (
    <Layout>
      <SEO
        title={seoTitle}
        description={loc.lang !== "en"
          ? t("part2.metaDesc", {
              part: loc.partName(part.name),
              brand: part.brand_name,
              model: part.model_name,
              type: loc.machType(part.machinery_type),
            })
          : part.description.length > 160 ? part.description.slice(0, 157).replace(/\s+\S*$/, "") + "…" : part.description}
        path={`/part/${part.slug}`}
        type="product"
        image={schemaDrawing ? `${SITE_URL}${schemaDrawing}` : undefined}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={crumbs} />
      <article>
        <div className="p-6 sm:p-14 border-b border-border bg-white">
          <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
            // {loc.machType(part.machinery_type)} / {part.brand_name} /{" "}
            {part.model_name}
          </p>
          <h1
            className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-4xl text-gray-950"
            data-testid="part-title"
          >
            {fullName}
          </h1>
          {loc.lang !== "en" && loc.partNameEn(part.name) && (
            <p
              className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-gray-400 mt-3"
              data-testid="part-title-en"
            >
              {enFullName}
            </p>
          )}
        </div>

        <div className="grid lg:grid-cols-3">
          <div className="lg:col-span-2 lg:border-r border-border">
            {variants.length > 1 && (
              <div className="p-6 sm:pl-10 lg:pl-14 border-b border-border bg-white" data-testid="part-variants">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3">
                  {t("cat.variantsTitle")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <Link
                      key={v.slug}
                      to={lp(`/part/${v.slug}`)}
                      data-testid={`part-variant-${v.slug}`}
                      className={`font-mono-tech text-xs px-3.5 py-2 border-2 transition-colors ${
                        v.slug === part.slug
                          ? "bg-[#0A0A0A] text-white border-gray-950"
                          : "border-gray-300 text-gray-950 hover:border-gray-950"
                      }`}
                    >
                      {v.part_code}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 border-b border-border bg-white">
              <div
                className="p-6 sm:pl-10 lg:pl-14 border-b sm:border-r border-border"
                data-testid="part-store-code-box"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                  Store Code
                </div>
                <div className="font-mono-tech font-bold text-xl text-gray-950">
                  {part.store_code ? (
                    <span className="bg-yellow-500 px-1.5">
                      {part.store_code}
                    </span>
                  ) : (
                    t("cat.onRequest")
                  )}
                </div>
              </div>
              <div
                className="p-6 border-b border-border"
                data-testid="part-code-box"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                  VANROCK PART CODES
                </div>
                <div className="font-mono-tech font-bold text-xl text-gray-950">
                  {part.part_code ? (
                    <span className="bg-yellow-500 px-1.5">
                      {part.part_code}
                    </span>
                  ) : (
                    t("cat.onRequest")
                  )}
                </div>
                {part.part_code && <PartCodeDecoder code={part.part_code} />}
              </div>
              <div
                className="p-6 sm:pl-10 lg:pl-14 border-b sm:border-b-0 sm:border-r border-border"
                data-testid="part-weight-box"
              >
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                  {t("cat.weight")}
                </div>
                <div className="font-mono-tech font-bold text-xl text-gray-950">
                  {part.weight
                    ? units === "imperial"
                      ? toImperial(part.weight)
                      : part.weight
                    : t("cat.onRequest")}
                </div>
              </div>
              <div className="p-6" data-testid="part-status-box">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">
                  {t("cat.availability")}
                </div>
                <div className="font-mono-tech font-bold text-xl text-gray-950 uppercase">
                  {loc.status(part.status)}
                </div>
              </div>
            </div>

            {part.image_url && (
              <div className="border-b border-border p-6 sm:pl-10 lg:pl-14 bg-white">
                <img
                  src={part.image_url}
                  alt={fullName}
                  className="max-h-96 object-contain bg-white p-4"
                />
              </div>
            )}

            {!hideDetailsSection(part.machinery_type, part.type_key) && (
              <section className="border-b border-border bg-white">
                <div className="flex items-center justify-between bg-[#0A0A0A] p-4 sm:px-10 lg:px-14">
                  <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-yellow-500">
                    {t("cat.details")}
                  </h2>
                  <div className="flex" data-testid="units-toggle">
                    {["metric", "imperial"].map((u) => (
                      <button
                        key={u}
                        onClick={() => setUnits(u)}
                        data-testid={`units-${u}`}
                        className={`px-3 py-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] border border-gray-700 transition-colors ${units === u ? "bg-yellow-500 text-black font-bold border-yellow-500" : "text-gray-400 hover:text-white"}`}
                      >
                        {t(`cat.${u}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={`grid ${hideDrawing ? "" : "lg:grid-cols-[1fr_360px]"}`}>
                  <div className="p-6 sm:p-8 sm:pl-10 lg:pl-14">
                    <div
                      className="grid sm:grid-cols-2 gap-x-8"
                      data-testid="part-specs-grid"
                    >
                      {part.specs.map((s, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-4 border-b border-border py-2.5"
                        >
                          <span className="font-mono-tech text-xs font-bold text-gray-950">
                            {loc.specCode(s.code)}
                          </span>
                          <span
                            className={`font-mono-tech text-sm ${s.value ? "font-bold text-gray-950" : "text-gray-400"}`}
                          >
                            {s.value
                              ? units === "imperial"
                                ? specToImperial(s)
                                : specToMetric(s)
                              : "—"}
                          </span>
                        </div>
                      ))}
                      {part.specs.length === 0 && (
                        <p
                          className="text-sm text-gray-600 sm:col-span-2"
                          data-testid="part-specs-empty"
                        >
                          {t("cat.dimsWithQuote")}
                        </p>
                      )}
                    </div>
                  </div>
                  {!hideDrawing && (
                  <div
                    className="border-t lg:border-t-0 lg:border-l border-border p-6 flex flex-col"
                    data-testid="part-drawing-panel"
                  >
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-3">
                      {t("cat.drawing")}
                    </div>
                    {part.drawing_url ||
                    categoryDrawing(part.machinery_type, part.type_key) ? (
                      <>
                        <img
                          src={
                            part.drawing_url
                              ? fileUrl(drawingSrc(part.drawing_url))
                              : categoryDrawing(
                                  part.machinery_type,
                                  part.type_key,
                                )
                          }
                          alt={`${t("imgAlt.techDrawing")} — ${fullName}`}
                          className="w-full object-contain bg-white border border-border max-h-[520px] cursor-zoom-in hover:opacity-90 transition-opacity"
                          onClick={() => setDrawingOpen(true)}
                          data-testid="part-drawing-image"
                        />
                        <button
                          type="button"
                          onClick={() => setDrawingOpen(true)}
                          data-testid="part-drawing-enlarge"
                          className="mt-3 self-start font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-gray-950 underline underline-offset-4"
                        >
                          ⊕ {t("part2.enlargeDrawing", "Click to enlarge")}
                        </button>
                      </>
                    ) : (
                      <div
                        className="flex-1 min-h-[200px] border-2 border-dashed border-gray-300 flex items-center justify-center text-center p-6"
                        data-testid="part-drawing-placeholder"
                      >
                        <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-gray-400">
                          {t("cat.drawingOnRequest")}
                        </span>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </section>
            )}

            {part.compatible_with?.length > 0 && (
              <section className="border-b border-border bg-white">
                <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-yellow-500 bg-[#0A0A0A] p-4 sm:px-10 lg:px-14">
                  {t("cat.compatibleWith")}
                </h2>
                <div
                  className="p-6 sm:p-8 sm:pl-10 lg:pl-14 space-y-6"
                  data-testid="part-compatible-with"
                >
                  {Object.entries(
                    part.compatible_with.reduce((acc, c) => {
                      (acc[c.brand_slug] = acc[c.brand_slug] || {
                        name: c.brand_name,
                        models: [],
                      }).models.push(c);
                      return acc;
                    }, {}),
                  ).map(([brandSlug, group]) => (
                    <div
                      key={brandSlug}
                      data-testid={`compatible-brand-group-${brandSlug}`}
                    >
                      <Link
                        to={lp(`/brand/${brandSlug}`)}
                        className="inline-block font-display font-black uppercase text-sm tracking-tight text-gray-950 border-b-2 border-yellow-500 mb-3 hover:bg-yellow-500 transition-colors"
                        data-testid={`compatible-brand-link-${brandSlug}`}
                      >
                        {group.name}
                      </Link>
                      <div className="flex flex-wrap gap-2">
                        {group.models.map((c, i) => (
                          <Link
                            key={i}
                            to={lp(`/brand/${c.brand_slug}/${c.model_slug}`)}
                            data-testid={`compatible-link-${c.brand_slug}-${c.model_slug}`}
                            className="border border-border bg-gray-50 px-3 py-2 text-sm font-semibold font-mono-tech text-gray-950 hover:bg-yellow-500 hover:border-yellow-500 transition-colors"
                          >
                            {c.model_name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {part.cross_references.length > 0 && (
              <section className="border-b border-border bg-white">
                <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-yellow-500 bg-[#0A0A0A] p-4 sm:px-10 lg:px-14">
                  {t("cat.crossRef")}
                </h2>
                <div data-testid="part-crossrefs">
                  {part.cross_references.map((c, i) => (
                    <div
                      key={i}
                      className={`flex justify-between px-6 sm:px-10 lg:px-14 py-3 border-b border-border last:border-b-0 text-sm hover:bg-yellow-50 transition-colors ${i % 2 === 1 ? "bg-gray-50" : ""}`}
                    >
                      <span className="text-gray-600 uppercase">{c.brand}</span>
                      <span className="font-mono-tech font-semibold text-gray-950">
                        {c.code}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="border-b border-border bg-white">
              <h2 className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-yellow-500 bg-[#0A0A0A] p-4 sm:px-10 lg:px-14">
                {t("cat.otherParts")} {part.brand_name} {part.model_name}
              </h2>
              <div className="grid sm:grid-cols-2" data-testid="related-parts">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={lp(`/part/${r.slug}`)}
                    data-testid={`related-part-${r.slug}`}
                    className="cell-hover border-r border-b border-border px-6 sm:px-10 py-4 bg-white"
                  >
                    <span className="font-semibold text-sm text-gray-950">
                      {loc.partName(r.name)}
                    </span>
                    {loc.partNameEn(r.name) && (
                      <span className="block font-mono-tech text-[9px] uppercase tracking-wider text-gray-400 mt-0.5">
                        {loc.partNameEn(r.name)}
                      </span>
                    )}
                    <span className="block font-mono-tech text-[10px] uppercase tracking-wider text-gray-500 mt-1">
                      {r.brand_name} {r.model_name}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="border-t lg:border-t-0 border-border bg-gray-50">
            <div className="sm:pr-2 lg:pr-6">
              <div className="p-6 sm:p-8 space-y-5" data-testid="add-to-quote-panel">
                <div>
                  <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">{t("quote2.kicker")}</p>
                  <h2 className="font-display font-black uppercase text-2xl tracking-tight text-gray-950">{t("qc.addToQuote")}</h2>
                  <p className="text-xs text-gray-600 mt-2">{t("qc.panelSub")}</p>
                </div>
                {part.part_code && (
                  <div className="flex items-center justify-between border border-border bg-white px-3 py-2.5" data-testid="quote-vanrock-code">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-500">{t("rq3.lVanrockCode")}</span>
                    <span className="font-mono-tech font-bold text-sm text-gray-950 bg-yellow-500 px-1.5">{part.part_code}</span>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="add-to-quote-button"
                  onClick={() =>
                    quoteCart.openAdd({
                      slug: part.slug,
                      name: fullName,
                      rawName: part.name,
                      machType: part.machinery_type,
                      code: part.part_code || "",
                      brand: part.brand_name,
                      model: part.model_name,
                    })
                  }
                  className="w-full bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black rounded-none font-mono-tech text-xs uppercase tracking-[0.25em] py-5 transition-colors font-semibold"
                >
                  {t("qc.addToQuote")}
                </button>
              </div>
            </div>
          </aside>
        </div>
      </article>
      {drawingOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
          onClick={() => setDrawingOpen(false)}
          data-testid="part-drawing-lightbox"
        >
          <img
            src={
              part.drawing_url
                ? fileUrl(drawingSrc(part.drawing_url))
                : categoryDrawing(part.machinery_type, part.type_key)
            }
            alt={`${t("imgAlt.techDrawing")} — ${fullName}`}
            className="max-w-full max-h-full object-contain bg-white"
          />
          <button
            type="button"
            onClick={() => setDrawingOpen(false)}
            data-testid="part-drawing-lightbox-close"
            className="absolute top-4 right-4 bg-yellow-500 text-black font-mono-tech text-xs uppercase tracking-[0.2em] px-4 py-2 hover:bg-white transition-colors"
          >
            ✕
          </button>
        </div>
      )}
    </Layout>
  );
}
